import { useContext, useEffect, useState } from "react";

import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { Button, Skeleton } from "@upstageai/ui";

import { MobileOverlay } from "../components/Overlay/MobileOverlay";
import { DEMO_MODE_FEATURE } from "../constants/FeatureFlag";
import { FEATURE } from "../constants/FeatureFlag";
import { useDevice } from "../hooks/useDevice";
import { ValidatorFileContext } from "../hooks/useFile";
import { useInitialSample } from "../hooks/useInitialSample";
import { EndpointTitle } from "../molecules/detail-viewer/EndpointTitle";
import { CustomFilePreviewList } from "../molecules/file/CustomFilePreviewList";
import FileUploadButton from "../molecules/file/FileUploadButton";
import { SampleFilePreviewList } from "../molecules/file/SampleFilePreviewList";
import { MenuItems } from "../molecules/menu/MenuItems";
import { getResultFile } from "../utils/file";
import { cn } from "../utils/style";

export default function DocumentControlPanel() {
  const { selectedFileIndex, files, inferenceState } =
    useContext(ValidatorFileContext);
  const { isMobile } = useDevice();

  const showSampleFiles = FEATURE === DEMO_MODE_FEATURE;

  const [isExpanded, setIsExpanded] = useState(isMobile ? false : true);

  // 샘플 자동 로딩
  if (showSampleFiles) useInitialSample();

  useEffect(() => {
    // 모바일 환경 : 패널 열림, 데스크탑 환경 : 패널 닫힘
    setIsExpanded(isMobile ? false : true);
  }, [isMobile]);

  const _collapsePanel = () => {
    if (isExpanded) {
      setIsExpanded(false);
    }
  };

  const SelectedFileTitle = () => {
    const selectedFile = getResultFile(files, selectedFileIndex);
    if (!selectedFile) return null;

    if (inferenceState.isInfering)
      return (
        <div className="flex gap-2">
          <Skeleton className="h-4 w-[20px]" />
          <Skeleton className="h-4 w-[20px]" />
          <Skeleton className="h-4 w-[40px]" />
        </div>
      );

    return (
      <div className="body-medium h-full flex-1 content-center mobile:text-ss">
        {selectedFile.documentInfo?.industryType && (
          <span className="capitalize text-muted-medium">
            {selectedFile.documentInfo?.industryType} /{" "}
          </span>
        )}
        {selectedFile.documentInfo?.docType && (
          <span className="capitalize text-muted-medium">
            {selectedFile.documentInfo?.docType} /{" "}
          </span>
        )}
        <span>{selectedFile.title}</span>
      </div>
    );
  };

  const ExpandButton = () => {
    return (
      <div
        className="flex h-full items-center justify-center hover:cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)} // button css 적용 없이 button 클릭 영역 확장 위함
      >
        <Button variant="ghost" size="icon" className="hover:bg-transparent">
          {isExpanded ? (
            <ChevronUpIcon className="size-4 text-muted-darker" />
          ) : (
            <ChevronDownIcon className="size-4 text-muted-darker" />
          )}
        </Button>
      </div>
    );
  };

  return (
    <div
      className={cn(
        "z-30 flex w-full items-center justify-center bg-subtle pl-4 pr-2 desktop:rounded-ss-lg",
        isExpanded ? "h-[130px] min-h-[130px] py-3" : "h-14",
      )}
    >
      {/* 모바일 환경에서 패널 바깥 오버레이 렌더링 */}
      {isMobile && isExpanded && (
        <MobileOverlay
          onClose={() => setIsExpanded(false)}
          className="top-[130px]"
        />
      )}

      <div className="flex h-full">
        <FileUploadButton isExpanded={isExpanded} />
      </div>

      <div className="mr-3 flex h-full flex-1 items-center gap-2 overflow-x-auto mobile:mr-0">
        {isExpanded && (
          <>
            <CustomFilePreviewList />
            {showSampleFiles && <SampleFilePreviewList />}
          </>
        )}
        {!isExpanded && (
          <div className="ml-3 flex w-full items-center justify-between">
            {isMobile && <EndpointTitle />}
            {!isMobile && <SelectedFileTitle />}
          </div>
        )}
      </div>

      <div className="flex h-full items-center justify-center">
        {!isMobile && <MenuItems />}
        <ExpandButton />
      </div>
    </div>
  );
}
